// What the Schedule of Work calls a ticked preliminary or hire item (#464): the
// name of the line the estimator priced for it in the Schedule's option (the one
// its paint systems come from, systems.ts), else the name ticked on site (MJ,
// 2026-10-23). Pure.
import { hireMatches, prelimLine, type AccessTag, type AreaChoice, type HireItemFromApi, type JobPrice, type Prelim } from '@higgins/m2q-pricing';

/** What a desk line's name is read from besides the line: the hire list, and the job's own prices (#413). */
export interface LineNames {
  hireItems: readonly HireItemFromApi[];
  jobPrices: readonly JobPrice[];
}

export const NO_LINE_NAMES: LineNames = { hireItems: [], jobPrices: [] };

const named = (value: string | null | undefined): string | null => value?.trim() || null;
const jobPriceName = (names: LineNames, id: string | undefined) => (id ? named(names.jobPrices.find((p) => p.id === id)?.name) : null);

/** A preliminary: its line's name (a material's from the job's prices, as the desk prices it), else the ticked name. */
export function prelimName(prelim: Prelim, choice: AreaChoice | undefined, names: LineNames): string {
  const line = prelimLine(prelim, choice?.otherTrades ?? []);
  return jobPriceName(names, line?.jobPriceId) ?? named(line?.description) ?? prelim.label;
}

/**
 * A hire line as the Schedule needs it: `name` as the desk names it (pricing-inputs.ts `hireLine`: a quoted item's,
 * the hire list's, else as typed), and `match`, what a tag's search is tried on — the hire-list item's own name when
 * it is one (what the desk's "From site" picker matched), so a name from elsewhere can't break the link.
 */
function hireLineNames(line: NonNullable<AreaChoice['access']>[number], names: LineNames): { name: string; match: string } | null {
  const item = line.hireItemId ? names.hireItems.find((h) => h.id.toLowerCase() === line.hireItemId?.toLowerCase()) : undefined;
  const name = jobPriceName(names, line.jobPriceId) ?? named(item?.name) ?? named(line.item);
  return name ? { name, match: named(item?.name) ?? name } : null;
}

/** A tag that suggests hire: the names of the area's hire lines it suggests (the desk's "From site" match), else the ticked name. */
export function hireNames(tag: AccessTag, choice: AreaChoice | undefined, names: LineNames): string[] {
  const lines = (choice?.access ?? []).map((line) => hireLineNames(line, names)).filter((l) => l !== null);
  const matched = hireMatches(lines.map((l) => ({ name: l.match, shown: l.name })), tag.hireSearch ?? '').map((m) => m.shown);
  return matched.length ? [...new Set(matched)] : [tag.label];
}
